import type { LeadBody, LeadResponse } from '#root/rpc/client/lead'
import { computed, onMounted, ref, watch } from 'vue'
import { createInjectionState } from '@vueuse/shared'
import { useIdle, useStorage } from '@vueuse/core'
import { PromiseQueue } from '#root/lib/promise-queue'
import { useCartSession } from '#root/lib/useCartSession'
import { usePageContext } from '#root/renderer/usePageContext'
import { tracker } from '#root/lib/tracker'
import { fnSubmitLead } from '#root/rpc/client/lead'

const [useProvideOffers, useOffers] = createInjectionState(
  (options: { beforeOpen?: () => boolean }) => {
    const pq = new PromiseQueue()
    const isCopied = ref(false)
    const isPending = ref(false)
    const { testMode } = useCartSession()

    // UI state
    const offersDialog = ref(false)
    const { canonicalPaths, offerPopup } = usePageContext()
    const offerIndex = ref(0)
    const offers = computed(() => {
      return (
        offerPopup &&
        offerPopup.offers &&
        offerPopup.offers.filter(
          offer =>
            offer &&
            typeof offer === 'object' &&
            offer.offers_id &&
            typeof offer.offers_id === 'object' &&
            offer.offers_id.status === 'published' &&
            offer.offers_id.is_test === testMode.value
        )
      )
    })
    const selectedOffer = computed(() => {
      return offers.value?.[offerIndex.value]
    })

    let isEnabled = false

    if (Array.isArray(offerPopup?.builtin_pages)) {
      for (const page of offerPopup.builtin_pages) {
        if (
          page &&
          typeof page === 'object' &&
          page.builtin_pages_id &&
          typeof page.builtin_pages_id === 'object' &&
          page.builtin_pages_id.page_path &&
          (page.builtin_pages_id.page_path === canonicalPaths.relative ||
            (page.builtin_pages_id.page_path.endsWith('*') &&
              canonicalPaths.relative.startsWith(
                page.builtin_pages_id.page_path.slice(0, -1)
              )))
        ) {
          isEnabled = true
          break
        }
      }
    }

    const offerPopupTime = useStorage(`offer-${offerPopup.repeat_key}-time`, 0)

    function copyPromoCode(code: string) {
      if (navigator && navigator.clipboard) navigator.clipboard.writeText(code)

      isCopied.value = true

      setTimeout(() => {
        isCopied.value = false
      }, 2000)

      tracker.posthogCapture('lead:offer_promo_code_copy', {
        code,
        testMode: testMode.value
      })
    }

    function dismissOffers() {
      offersDialog.value = false
    }

    function nextOffer() {
      if (Array.isArray(offers.value) && offers.value.length > 1) {
        offerIndex.value = (offerIndex.value + 1) % offers.value.length
        isCopied.value = false

        tracker.posthogCapture('lead:offer_next', {
          testMode: testMode.value
        })
      }
    }

    function openOffers() {
      offerIndex.value = 0
      offersDialog.value = true

      tracker.posthogCapture('lead:offer_open', {
        testMode: testMode.value
      })
    }

    onMounted(() => {
      setTimeout(
        () => {
          if (offerPopup.is_shown_when_idle) {
            const { idle, reset } = useIdle(
              (offerPopup.idle_seconds || 0) * 1000
            )

            watch(idle, newValue => {
              if (newValue) {
                if (!isEnabled) return
                if (offersDialog.value) return

                if (
                  !(
                    new Date().getTime() - offerPopupTime.value >
                    (offerPopup.repeat_after_minutes || 0) * 60 * 1000
                  )
                )
                  return

                if (options.beforeOpen && !options.beforeOpen()) {
                  reset()
                  return
                }

                openOffers()
              }
            })
          }
        },
        (offerPopup.inactive_seconds || 0) * 1000
      )

      watch(offersDialog, (newValue, oldValue) => {
        if (newValue === false && oldValue === true) {
          offerPopupTime.value = new Date().getTime()
        }
      })
    })

    async function submitLead(body: LeadBody): Promise<LeadResponse> {
      isPending.value = true

      return pq
        .add<LeadResponse>(async () => {
          tracker.plausibleEvent('offerSubmit', {
            testMode: testMode.value
          })

          if (body?.data?.email) {
            await tracker.posthogIdentify(body.data.email)
          }
          tracker.posthogCapture('lead:offer_submit', {
            test_mode: testMode.value
          })

          const resp = await fnSubmitLead(body)

          return resp
        })
        .catch(() => {
          return { ok: false }
        })
        .finally(() => (isPending.value = false))
    }

    return {
      copyPromoCode,
      dismissOffers,
      isCopied,
      isPending,
      nextOffer,
      offerIndex,
      offers,
      offersDialog,
      openOffers,
      selectedOffer,
      submitLead
    }
  }
)

function useOffersOrThrow() {
  const state = useOffers()
  if (state == null)
    throw new Error(
      'Please call `useProvideOffers` on the appropriate parent component'
    )
  return state
}

export { useProvideOffers, useOffersOrThrow }
